import React, {
    memo,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useReducer,
    useRef,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTheme, Text } from '@Sage/hoi-poi-ui';
import { downloadFile, sanitizeFilename } from '@Sage/ssm-utils';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { CAMPAIGNS_MARKETING } from 'constants/Entities';
import { EntityCrudService, MailingService } from 'services';
import { createMarketingEmailTemplate } from 'services/CampaignsService';
import { listDomains } from 'services/DomainService';
import { listSenders } from 'services/SenderService';
import Context from 'managers/Context';
import { getLiteral } from 'utils/getLiteral';
import { errorToast, successToast } from 'utils/toast';
import AbsoluteLoader from 'components/AbsoluteLoader';
import ModalHoi from 'components/ModalHoi';
import CampaignDetailsHeader from './CampaignDetailsHeader';
import CampaignDetailsFormContent from './CampaignDetailsFormContent';
import HtmlEditorOverlay from './DesignForm/HtmlEditorOverlay';
import EmailEditor from '../EmailEditor';
import { applyFixedFooterPreviewToHtml } from '../EmailEditor/fixedFooter';
import { formatMarketingSenderPostalAddress } from '../settings/unsubscribe/templates';
import SenderFormModal from '../SenderFormModal';
import CampaignPreviewTestModal from './CampaignPreviewTestModal';
import DomainVerificationBanner from '../components/DomainVerificationBanner';
import SaveAsTemplateModal from '../components/SaveAsTemplateModal';
import SelectRecipientsModal from './RecipientsForm/SelectRecipientsModal';
import { useDomainValidation } from '../hooks/useDomainValidation';
import { useMarketingCampaignLimits } from '../hooks/useMarketingCampaignLimits';
import { getCampaignSendLimitBlock } from '../utils/marketingCampaignLimitsViewModel';
import {
    buildCampaignDesignFromTemplateRow,
    isTopolTemplateRow,
} from '../utils/buildCampaignDesignFromTemplateRow';
import { MARKETING_TEMPLATES_PATHS } from '../templates/marketingTemplatesPaths';
import { TEMPLATE_DESIGN_FLOW_ORIGIN } from '../utils/templateDesignFlow';
import { logMarketingCampaignsEvent } from '../utils/marketingTracking';
import CampaignSendConfirmModal from './CampaignSendConfirmModal';
import CampaignTemplateSelector from './CampaignTemplateSelector';
import {
    getMarketingCampaignStatusBadgeType,
    getMarketingCampaignStatusLabel,
    MARKETING_CAMPAIGN_STATUS,
    normalizeMarketingCampaignStatusId,
} from '../utils/marketingCampaignStatus';
import {
    hasTopolEditorMetadata,
    mergeTopolJsonIntoCampaignMetadata,
    stableStringifyMetadata,
    extractTopolJsonFromMetadata,
} from '../utils/marketingCampaignMetadata';
import { buildMarketingSenderSelectOptions } from '../utils/marketingSenderDomainVerification';
import {
    getMarketingSenderIdFromRow,
    mergeEnsureSendersIntoSelectOptions,
    parseCampaignSenderIdField,
    resolveSelectedSenderIdFromOptions,
} from '../utils/marketingSenderOptions';
import { publish } from 'lib/EventBuser';
import { REFRESH_TABLE } from 'lib/events';
import './styles.scss';

/** Estado inicial hasta `HYDRATE_FROM_SERVER` (ruta edit) o datos del usuario (ruta new). */
const EMPTY_NEW_CAMPAIGN = {
    id: null,
    name: '',
    senderId: null,
    subject: '',
    body: '',
    status: MARKETING_CAMPAIGN_STATUS.DRAFT,
};

const DEFAULT_EXPANDED_SECTIONS = {
    sender: false,
    recipients: false,
    subject: false,
    design: false,
    additionalSettings: false,
};

const RESTORABLE_SECTIONS = new Set(['sender', 'recipients', 'subject']);

const showGenericBackendErrorToast = () => {
    errorToast({ text: getLiteral('error_generalerror') });
};

function comparablePersistId(value) {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? String(value) : numericValue;
}

/** Respuesta GET/PUT campaña: a veces viene envuelta en `data`. */
function normalizeCampaignApiRecord(campaign) {
    if (!campaign || typeof campaign !== 'object') {
        return {};
    }
    if (
        campaign.data &&
        typeof campaign.data === 'object' &&
        !Array.isArray(campaign.data) &&
        campaign.senderAddress === undefined &&
        campaign.footerHere === undefined
    ) {
        return campaign.data;
    }
    return campaign;
}

/** Valores de preview del footer fijo desde GET/PUT detalle de campaña (`mailing/v2/campaign`). */
function getCampaignFooterPreviewFromApi(campaign) {
    const record = normalizeCampaignApiRecord(campaign);
    const senderId = record.senderId;
    let footerSenderName = '';

    if (senderId != null && typeof senderId === 'object') {
        footerSenderName = String(senderId.label ?? senderId.name ?? '').trim();
    } else {
        const senderParsed = parseCampaignSenderIdField(senderId);
        footerSenderName = (senderParsed.label || '').trim();
    }

    return {
        footerSenderName,
        footerSenderAddress:
            record.senderAddress == null ? '' : String(record.senderAddress).trim(),
        footerFooterHere: record.footerHere == null ? '' : String(record.footerHere).trim(),
    };
}

/** Fallback cuando la campaña aún no tiene `senderAddress` / `footerHere` del backend. */
function getSelectedSenderFooterFallback(selectedSenderOption) {
    const row = selectedSenderOption?.row;
    const senderName = (selectedSenderOption?.subLabel || row?.name || '').trim();

    return {
        senderName,
        senderAddress: row ? formatMarketingSenderPostalAddress(row) : '',
    };
}

function buildCampaignFooterPreview({
    footerSenderName = '',
    footerSenderAddress = '',
    footerFooterHere = '',
    selectedSenderOption,
}) {
    const senderFallback = getSelectedSenderFooterFallback(selectedSenderOption);
    const unsubscribeLiteral = getLiteral('label_marketing_campaigns_unsubscribe_here');

    return {
        senderName: (footerSenderName || senderFallback.senderName || '').trim(),
        senderAddress: (senderFallback.senderAddress || footerSenderAddress || '').trim(),
        footerHere: (footerFooterHere || unsubscribeLiteral || '').trim(),
        linkHref: '#',
    };
}

function buildLastPersistedFromCampaignFields(source) {
    const senderParsed = parseCampaignSenderIdField(source.senderId);
    const rawId = senderParsed.id;
    return {
        name: (source.name || '').trim(),
        senderId:
            rawId === null || rawId === undefined || rawId === ''
                ? null
                : comparablePersistId(rawId),
        subject: (source.subject || '').trim(),
        body: source.body || '',
        status: normalizeMarketingCampaignStatusId(source.status),
        previewText: (source.previewText || '').trim(),
        metadata: source.metadata ?? null,
    };
}

function isPersistedCampaignPayloadUnchanged(payload, last) {
    if (!last) {
        return false;
    }
    const nameA = (payload.name || '').trim();
    const nameB = (last.name || '').trim();
    const subA = (payload.subject || '').trim();
    const subB = (last.subject || '').trim();
    const bodyA = payload.body || '';
    const bodyB = last.body || '';
    const statusA = normalizeMarketingCampaignStatusId(payload.status);
    const statusB = normalizeMarketingCampaignStatusId(last.status);
    const metaA = stableStringifyMetadata(payload.metadata);
    const metaB = stableStringifyMetadata(last.metadata);
    return (
        nameA === nameB &&
        comparablePersistId(payload.senderId) === comparablePersistId(last.senderId) &&
        subA === subB &&
        bodyA === bodyB &&
        String(statusA) === String(statusB) &&
        metaA === metaB
    );
}

function isCampaignDesignUnchanged({ title, html, metadata, lastPersisted }) {
    if (!lastPersisted) {
        return false;
    }

    const titleTrim = typeof title === 'string' ? title.trim() : title;
    const nameB = (lastPersisted.name || '').trim();
    const bodyA = html || '';
    const bodyB = lastPersisted.body || '';

    if (titleTrim !== nameB || bodyA !== bodyB) {
        return false;
    }

    if (metadata !== undefined) {
        return (
            stableStringifyMetadata(metadata) === stableStringifyMetadata(lastPersisted.metadata)
        );
    }

    return true;
}

function resolveInitialCampaignTitle(campaign, routeCampaignId) {
    const fromCampaign = (campaign.name || '').trim();
    if (fromCampaign) {
        return fromCampaign;
    }
    if (!routeCampaignId && campaign.id == null) {
        return getLiteral('label_new_email');
    }
    return '';
}

function createSectionDraftSnapshot(state, section) {
    switch (section) {
        case 'sender':
            return {
                selectedSenderId: state.selectedSenderId,
            };
        case 'recipients':
            return {
                token: state.token,
                recipientsCount: state.recipientsCount,
            };
        case 'subject':
            return {
                previewText: state.previewText,
                subject: state.subject,
            };
        default:
            return null;
    }
}

function removeSectionDraftSnapshot(sectionDraftSnapshots = {}, section) {
    const { [section]: _removedSnapshot, ...remainingSnapshots } = sectionDraftSnapshots;

    return remainingSnapshots;
}

function closeSectionWithoutRestore(state, section) {
    return {
        ...state,
        expandedSections: {
            ...state.expandedSections,
            [section]: false,
        },
        sectionDraftSnapshots: removeSectionDraftSnapshot(state.sectionDraftSnapshots, section),
    };
}

function restoreSectionDraftSnapshot(state, section) {
    const snapshot = state.sectionDraftSnapshots?.[section];
    const nextState = closeSectionWithoutRestore(state, section);

    if (!snapshot) {
        return nextState;
    }

    switch (section) {
        case 'sender':
            return {
                ...nextState,
                selectedSenderId: snapshot.selectedSenderId,
            };
        case 'recipients':
            return {
                ...nextState,
                token: snapshot.token,
                recipientsCount: snapshot.recipientsCount,
            };
        case 'subject':
            return {
                ...nextState,
                previewText: snapshot.previewText,
                subject: snapshot.subject,
            };
        default:
            return nextState;
    }
}

const createInitialState = (campaign, routeCampaignId) => {
    const senderParsed = parseCampaignSenderIdField(campaign.senderId);
    const hasDesignBody = Boolean((campaign.body || '').trim());
    const footerPreviewFields = getCampaignFooterPreviewFromApi(campaign);
    return {
        campaignTitle: resolveInitialCampaignTitle(campaign, routeCampaignId),
        campaignHtml: campaign.body || '',
        campaignId: campaign.id ?? routeCampaignId ?? null,
        expandedSections: {
            ...DEFAULT_EXPANDED_SECTIONS,
            ...(hasDesignBody ? { design: true } : {}),
        },
        isHtmlEditorOpen: false,
        isTopolEditorOpen: false,
        isTemplateSelectorOpen: false,
        templateDesignFlowOrigin: null,
        designSnapshotBeforeTemplate: null,
        pendingTemplateDesign: null,
        isSelectRecipientsOpen: false,
        token: campaign.token || '',
        recipientsCount: Number(campaign.totalRecipients ?? campaign.recipientsCount) || 0,
        savedToken: campaign.token || '',
        savedRecipientsCount: Number(campaign.totalRecipients ?? campaign.recipientsCount) || 0,
        isSenderModalOpen: false,
        isPreviewModalOpen: false,
        previewCampaign: null,
        senders: [],
        selectedSenderId:
            senderParsed.id !== null && senderParsed.id !== undefined && senderParsed.id !== ''
                ? String(senderParsed.id)
                : '',
        senderEmail: campaign.senderEmail || senderParsed.label || '',
        subject: campaign.subject || '',
        previewText: campaign.previewText || '',
        campaignStatusValue: normalizeMarketingCampaignStatusId(campaign.status),
        lastPersisted: buildLastPersistedFromCampaignFields(campaign),
        campaignMetadata:
            campaign.metadata !== undefined && campaign.metadata !== null
                ? campaign.metadata
                : null,
        footerSenderName: footerPreviewFields.footerSenderName,
        footerSenderAddress: footerPreviewFields.footerSenderAddress,
        footerFooterHere: footerPreviewFields.footerFooterHere,
        sectionDraftSnapshots: {},
    };
};

function campaignDetailsReducer(state, action) {
    switch (action.type) {
        case 'HYDRATE_FROM_SERVER': {
            const r = action.payload;
            const senderParsed = parseCampaignSenderIdField(r.senderId);
            const hydratedSenderId =
                senderParsed.id !== null && senderParsed.id !== undefined && senderParsed.id !== ''
                    ? String(senderParsed.id)
                    : '';
            const hydratedSenderEmail = r.senderEmail || senderParsed.label || '';
            const hasDesignBody = Boolean((r.body || '').trim());
            const hydratedToken = r.token || '';
            const hydratedRecipientsCount = Number(r.totalRecipients ?? r.recipientsCount) || 0;
            const footerPreviewFields = getCampaignFooterPreviewFromApi(r);
            return {
                ...state,
                campaignTitle: r.name || '',
                campaignHtml: r.body || '',
                campaignId: r.id ?? action.routeCampaignId,
                expandedSections: {
                    ...state.expandedSections,
                    ...(hasDesignBody ? { design: true } : {}),
                },
                token: hydratedToken,
                recipientsCount: hydratedRecipientsCount,
                savedToken: hydratedToken,
                savedRecipientsCount: hydratedRecipientsCount,
                selectedSenderId: hydratedSenderId,
                senderEmail: hydratedSenderEmail,
                subject: r.subject || '',
                previewText: r.previewText || '',
                campaignStatusValue: normalizeMarketingCampaignStatusId(r.status),
                lastPersisted: buildLastPersistedFromCampaignFields(r),
                campaignMetadata:
                    r.metadata !== undefined && r.metadata !== null ? r.metadata : null,
                footerSenderName: footerPreviewFields.footerSenderName,
                footerSenderAddress: footerPreviewFields.footerSenderAddress,
                footerFooterHere: footerPreviewFields.footerFooterHere,
                sectionDraftSnapshots: {},
            };
        }
        case 'SET_CAMPAIGN_FOOTER_FROM_API': {
            const footerPreviewFields = getCampaignFooterPreviewFromApi(action.payload);
            return {
                ...state,
                footerSenderName: footerPreviewFields.footerSenderName,
                footerSenderAddress: footerPreviewFields.footerSenderAddress,
                footerFooterHere: footerPreviewFields.footerFooterHere,
            };
        }
        case 'SET_LAST_PERSISTED_CAMPAIGN':
            return { ...state, lastPersisted: action.payload };
        case 'SET_CAMPAIGN_ID_FROM_ROUTE':
            return { ...state, campaignId: action.payload };
        case 'OPEN_SELECT_RECIPIENTS':
            return { ...state, isSelectRecipientsOpen: true };
        case 'CLOSE_SELECT_RECIPIENTS':
            return { ...state, isSelectRecipientsOpen: false };
        case 'SET_RECIPIENTS_DATA':
            return {
                ...state,
                token: action.payload.token || '',
                recipientsCount: Number(action.payload.count) || 0,
                isSelectRecipientsOpen: false,
            };
        case 'PERSIST_METADATA': {
            const { savedId, status } = action.payload;
            let next = { ...state };
            if (savedId && String(savedId) !== String(state.campaignId)) {
                next = { ...next, campaignId: savedId };
            }
            if (status !== undefined && status !== null) {
                next = {
                    ...next,
                    campaignStatusValue: normalizeMarketingCampaignStatusId(status),
                };
            }
            return next;
        }
        case 'TOGGLE_SECTION': {
            const section = action.payload;
            if (section === 'design' && (state.campaignHtml || '').trim()) {
                if (state.expandedSections.design) {
                    return state;
                }
                return {
                    ...state,
                    expandedSections: { ...state.expandedSections, design: true },
                };
            }

            if (state.expandedSections[section]) {
                if (RESTORABLE_SECTIONS.has(section)) {
                    return restoreSectionDraftSnapshot(state, section);
                }

                return closeSectionWithoutRestore(state, section);
            }

            const nextState = {
                ...state,
                expandedSections: {
                    ...state.expandedSections,
                    [section]: true,
                },
            };

            if (!RESTORABLE_SECTIONS.has(section)) {
                return nextState;
            }

            return {
                ...nextState,
                sectionDraftSnapshots: {
                    ...state.sectionDraftSnapshots,
                    [section]: createSectionDraftSnapshot(state, section),
                },
            };
        }
        case 'CANCEL_SECTION':
            return restoreSectionDraftSnapshot(state, action.payload);
        case 'COMMIT_SECTION':
            return closeSectionWithoutRestore(state, action.payload);
        case 'OPEN_HTML_EDITOR':
            return { ...state, isHtmlEditorOpen: true };
        case 'CLOSE_HTML_EDITOR':
            return { ...state, isHtmlEditorOpen: false };
        case 'OPEN_TOPOL_EDITOR':
            return { ...state, isTopolEditorOpen: true };
        case 'CLOSE_TOPOL_EDITOR':
            return { ...state, isTopolEditorOpen: false };
        case 'OPEN_TEMPLATE_SELECTOR':
            return { ...state, isTemplateSelectorOpen: true };
        case 'CLOSE_TEMPLATE_SELECTOR':
            return { ...state, isTemplateSelectorOpen: false };
        case 'BEGIN_TEMPLATE_DESIGN_FLOW':
            return {
                ...state,
                templateDesignFlowOrigin: action.payload.origin,
                designSnapshotBeforeTemplate: action.payload.snapshot,
                pendingTemplateDesign: action.payload.pendingDesign,
                isTemplateSelectorOpen: false,
            };
        case 'CLEAR_TEMPLATE_DESIGN_FLOW':
            return {
                ...state,
                templateDesignFlowOrigin: null,
                designSnapshotBeforeTemplate: null,
                pendingTemplateDesign: null,
            };
        case 'COMMIT_RECIPIENTS_SAVE':
            return {
                ...state,
                savedToken: state.token,
                savedRecipientsCount: state.recipientsCount,
                expandedSections: { ...state.expandedSections, recipients: false },
                sectionDraftSnapshots: removeSectionDraftSnapshot(
                    state.sectionDraftSnapshots,
                    'recipients',
                ),
            };
        case 'SET_SUBJECT':
            return { ...state, subject: action.payload };
        case 'SET_PREVIEW_TEXT':
            return { ...state, previewText: action.payload };
        case 'SET_CAMPAIGN_TITLE':
            return { ...state, campaignTitle: action.payload };
        case 'SET_TITLE_AND_HTML': {
            const html = action.payload.html;
            const hasDesignBody = Boolean((html || '').trim());
            const nextMetadata = Object.prototype.hasOwnProperty.call(action.payload, 'metadata')
                ? action.payload.metadata
                : state.campaignMetadata;
            return {
                ...state,
                campaignTitle: action.payload.title,
                campaignHtml: html,
                campaignMetadata: nextMetadata,
                expandedSections: {
                    ...state.expandedSections,
                    ...(hasDesignBody ? { design: true } : {}),
                },
            };
        }
        case 'CLEAR_TOPOL_METADATA': {
            if (!state.campaignMetadata) return state;
            const { topolEditorConfig: _, ...restMetadata } = state.campaignMetadata;
            const nextMetadata = Object.keys(restMetadata).length > 0 ? restMetadata : null;
            return {
                ...state,
                campaignMetadata: nextMetadata,
            };
        }
        case 'OPEN_PREVIEW_MODAL':
            return {
                ...state,
                previewCampaign: action.payload,
                isPreviewModalOpen: true,
            };
        case 'CLOSE_PREVIEW_MODAL':
            return {
                ...state,
                isPreviewModalOpen: false,
            };
        case 'CLEAR_PREVIEW_CAMPAIGN':
            return {
                ...state,
                previewCampaign: null,
            };
        case 'OPEN_SENDER_MODAL':
            return { ...state, isSenderModalOpen: true };
        case 'CLOSE_SENDER_MODAL':
            return { ...state, isSenderModalOpen: false };
        case 'SET_SENDERS':
            return {
                ...state,
                senders: action.payload,
                selectedSenderId: resolveSelectedSenderIdFromOptions(
                    action.payload,
                    action.preferredSenderId ?? state.selectedSenderId,
                ),
            };
        case 'SET_SELECTED_SENDER_ID':
            return { ...state, selectedSenderId: action.payload };
        case 'SAVE_SENDER_FIELDS':
            return {
                ...state,
                selectedSenderId: action.payload.senderId
                    ? String(action.payload.senderId)
                    : state.selectedSenderId,
            };
        default:
            return state;
    }
}

const MarketingCampaignsForm = memo(({ onBack, onPreview, onSend, onSendTestEmail }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const { id: routeCampaignId } = useParams();
    const { hasValidatedDomain, loading: domainValidationLoading } = useDomainValidation();
    const { data: campaignLimitsData, loading: campaignLimitsLoading } =
        useMarketingCampaignLimits();

    const [state, dispatch] = useReducer(
        campaignDetailsReducer,
        { routeCampaignId },
        ({ routeCampaignId: rid }) => createInitialState(EMPTY_NEW_CAMPAIGN, rid ?? null),
    );

    const [editBootstrapComplete, setEditBootstrapComplete] = useState(() => !routeCampaignId);
    const [sendersLoadComplete, setSendersLoadComplete] = useState(false);
    const [senderSelectInstanceKey, setSenderSelectInstanceKey] = useState(0);
    const [isSendConfirmOpen, setIsSendConfirmOpen] = useState(false);
    const [isSendSubmitting, setIsSendSubmitting] = useState(false);
    const [isSendConsentChecked, setIsSendConsentChecked] = useState(false);
    const [isResetDesignConfirmOpen, setIsResetDesignConfirmOpen] = useState(false);
    const [isSectionSaveLoading, setIsSectionSaveLoading] = useState(false);
    const [isSaveAsTemplateModalOpen, setIsSaveAsTemplateModalOpen] = useState(false);
    const [pendingTemplateDraft, setPendingTemplateDraft] = useState(null);
    const [isSaveAsTemplateSubmitting, setIsSaveAsTemplateSubmitting] = useState(false);

    const skipNextCampaignFetchRef = useRef(false);
    const appliedTemplateDesignRef = useRef(false);

    const {
        campaignTitle,
        campaignHtml,
        campaignId,
        expandedSections,
        isHtmlEditorOpen,
        isTopolEditorOpen,
        isTemplateSelectorOpen,
        templateDesignFlowOrigin,
        designSnapshotBeforeTemplate,
        pendingTemplateDesign,
        isSelectRecipientsOpen,
        token,
        recipientsCount,
        savedToken,
        savedRecipientsCount,
        isSenderModalOpen,
        isPreviewModalOpen,
        previewCampaign,
        senders,
        selectedSenderId,
        senderEmail,
        subject,
        previewText,
        campaignStatusValue,
        lastPersisted,
        campaignMetadata,
        footerSenderName,
        footerSenderAddress,
        footerFooterHere,
    } = state;

    const campaignIdRef = useRef(campaignId);
    const pendingMarketingCreateRef = useRef(null);

    // Do not treat auto-selected sender (SET_SENDERS default) as a local draft: it would skip
    // and abort the edit fetch when senders load before the campaign GET completes.
    const hasLocalDraft = useMemo(
        () =>
            Boolean(campaignTitle?.trim?.()) ||
            Boolean(campaignHtml?.trim?.()) ||
            Boolean(subject?.trim?.()) ||
            Boolean(previewText?.trim?.()),
        [campaignHtml, campaignTitle, previewText, subject],
    );

    const isTemplateDesignFlow = Boolean(templateDesignFlowOrigin);

    const beginTemplateDesignFlow = useCallback(
        (row, origin) => {
            const pendingDesign = buildCampaignDesignFromTemplateRow(row, {
                campaignTitle,
                campaignMetadata,
            });

            dispatch({
                type: 'BEGIN_TEMPLATE_DESIGN_FLOW',
                payload: {
                    origin,
                    snapshot: {
                        html: campaignHtml,
                        metadata: campaignMetadata,
                        title: campaignTitle,
                    },
                    pendingDesign,
                },
            });

            if (isTopolTemplateRow(row)) {
                dispatch({ type: 'OPEN_TOPOL_EDITOR' });
            } else {
                dispatch({ type: 'OPEN_HTML_EDITOR' });
            }
        },
        [campaignHtml, campaignMetadata, campaignTitle],
    );

    useLayoutEffect(() => {
        if (routeCampaignId || appliedTemplateDesignRef.current) {
            return;
        }

        const templateRow = location.state?.campaignTemplateRow;
        if (!templateRow || typeof templateRow !== 'object') {
            return;
        }

        appliedTemplateDesignRef.current = true;
        const origin =
            location.state?.designFlowOrigin ?? TEMPLATE_DESIGN_FLOW_ORIGIN.TEMPLATES_LIST;
        beginTemplateDesignFlow(templateRow, origin);
        navigate(location.pathname, { replace: true, state: null });
    }, [beginTemplateDesignFlow, location.pathname, location.state, navigate, routeCampaignId]);

    useLayoutEffect(() => {
        if (!routeCampaignId) {
            setEditBootstrapComplete(true);
            return;
        }
        if (hasLocalDraft) {
            setEditBootstrapComplete(true);
            return;
        }
        setEditBootstrapComplete(false);
    }, [hasLocalDraft, routeCampaignId]);

    useEffect(() => {
        if (!routeCampaignId || hasLocalDraft) {
            return undefined;
        }

        if (skipNextCampaignFetchRef.current) {
            skipNextCampaignFetchRef.current = false;
            setEditBootstrapComplete(true);
            return;
        }

        let isCancelled = false;

        EntityCrudService.get(CAMPAIGNS_MARKETING, routeCampaignId)
            .then((result) => {
                if (isCancelled) {
                    return;
                }
                if (!result) {
                    showGenericBackendErrorToast();
                    return;
                }

                dispatch({
                    type: 'HYDRATE_FROM_SERVER',
                    payload: result,
                    routeCampaignId,
                });
            })
            .catch(() => {
                if (!isCancelled) {
                    showGenericBackendErrorToast();
                }
            })
            .finally(() => {
                if (!isCancelled) {
                    setEditBootstrapComplete(true);
                }
            });

        return () => {
            isCancelled = true;
        };
    }, [hasLocalDraft, routeCampaignId]);

    useLayoutEffect(() => {
        campaignIdRef.current = campaignId;
    }, [campaignId]);

    useEffect(() => {
        if (!isTopolEditorOpen || !campaignId) {
            return undefined;
        }

        let cancelled = false;

        EntityCrudService.get(CAMPAIGNS_MARKETING, campaignId)
            .then((result) => {
                if (cancelled || !result) {
                    return;
                }
                dispatch({ type: 'SET_CAMPAIGN_FOOTER_FROM_API', payload: result });
            })
            .catch(() => {});

        return () => {
            cancelled = true;
        };
    }, [isTopolEditorOpen, campaignId]);

    useEffect(() => {
        if (routeCampaignId && String(routeCampaignId) !== String(campaignId)) {
            dispatch({ type: 'SET_CAMPAIGN_ID_FROM_ROUTE', payload: routeCampaignId });
        }
    }, [campaignId, routeCampaignId]);

    const applySenderOptions = useCallback((options, preferredSenderId) => {
        dispatch({
            type: 'SET_SENDERS',
            payload: options,
            preferredSenderId,
        });
        setSenderSelectInstanceKey((n) => n + 1);
    }, []);

    const fetchAndApplySenderOptions = useCallback(
        async ({ ensureEntries = [], preferredSenderId } = {}) => {
            const [sendersResult, domainsResult] = await Promise.allSettled([
                listSenders(),
                listDomains(),
            ]);
            const options = buildMarketingSenderSelectOptions(
                sendersResult,
                domainsResult,
                ensureEntries,
            );
            applySenderOptions(options, preferredSenderId);
        },
        [applySenderOptions],
    );

    useEffect(() => {
        let isCancelled = false;

        setSendersLoadComplete(false);

        fetchAndApplySenderOptions()
            .catch(() => {
                if (!isCancelled) {
                    showGenericBackendErrorToast();
                }
            })
            .finally(() => {
                if (!isCancelled) {
                    setSendersLoadComplete(true);
                }
            });

        return () => {
            isCancelled = true;
        };
    }, [fetchAndApplySenderOptions]);

    useEffect(() => {
        if (!sendersLoadComplete || !selectedSenderId || !senderEmail) {
            return;
        }
        if (senders.some((sender) => String(sender.value) === String(selectedSenderId))) {
            return;
        }
        applySenderOptions(
            mergeEnsureSendersIntoSelectOptions(senders, [
                { id: selectedSenderId, label: senderEmail },
            ]),
            selectedSenderId,
        );
    }, [applySenderOptions, senders, sendersLoadComplete, selectedSenderId, senderEmail]);

    const normalizeIdValue = useCallback((value) => {
        if (value === null || value === undefined || value === '') {
            return null;
        }

        const numericValue = Number(value);
        return Number.isNaN(numericValue) ? value : numericValue;
    }, []);

    const getSavedCampaignId = useCallback(
        (result) => {
            const savedId = result?.id;

            return normalizeIdValue(savedId);
        },
        [normalizeIdValue],
    );

    const buildCampaignPayload = useCallback(
        (overrides = {}) => {
            const nextSenderId = overrides.senderId ?? selectedSenderId;
            const nextName = overrides.name ?? campaignTitle;
            const nextSubject = overrides.subject ?? subject;
            const nextPreviewText = overrides.previewText ?? previewText;
            const nextToken = overrides.token ?? savedToken;
            const nextTotalRecipients = overrides.totalRecipients ?? savedRecipientsCount;

            const payload = {
                id: normalizeIdValue(overrides.id ?? campaignId),
                name: typeof nextName === 'string' ? nextName.trim() : nextName,
                senderId: normalizeIdValue(nextSenderId),
                subject: typeof nextSubject === 'string' ? nextSubject.trim() : nextSubject,
                previewText:
                    typeof nextPreviewText === 'string' ? nextPreviewText.trim() : nextPreviewText,
                body: overrides.body ?? campaignHtml,
                status: normalizeMarketingCampaignStatusId(overrides.status ?? campaignStatusValue),
                totalRecipients: Number(nextTotalRecipients) || 0,
                ...(nextToken ? { token: nextToken } : {}),
            };

            if (Object.prototype.hasOwnProperty.call(overrides, 'metadata')) {
                payload.metadata = overrides.metadata;
            } else {
                payload.metadata = campaignMetadata;
            }

            return payload;
        },
        [
            campaignHtml,
            campaignId,
            campaignMetadata,
            campaignStatusValue,
            campaignTitle,
            normalizeIdValue,
            previewText,
            savedRecipientsCount,
            savedToken,
            selectedSenderId,
            subject,
        ],
    );

    const persistCampaign = useCallback(
        async (overrides = {}, options = {}) => {
            const { showErrorToast = true } = options;
            try {
                const routeOrRefId = overrides.id ?? campaignIdRef.current;
                let payload = buildCampaignPayload(
                    routeOrRefId != null && routeOrRefId !== ''
                        ? { ...overrides, id: routeOrRefId }
                        : overrides,
                );
                let hasCampaignId = Boolean(payload.id);

                if (!hasCampaignId) {
                    const existingCreate = pendingMarketingCreateRef.current;
                    if (existingCreate) {
                        const savedId = await existingCreate;
                        campaignIdRef.current = savedId;
                        payload = buildCampaignPayload({ ...overrides, id: savedId });
                        hasCampaignId = true;
                    }
                }

                if (!hasCampaignId) {
                    const requestPayload = (({ id, ...rest }) => rest)(payload);
                    const createWork = (async () => {
                        const result = await EntityCrudService.create(
                            CAMPAIGNS_MARKETING,
                            requestPayload,
                        );
                        const savedId = getSavedCampaignId(result);
                        if (!savedId) {
                            throw new Error('Marketing campaign create returned no id');
                        }
                        campaignIdRef.current = savedId;
                        dispatch({
                            type: 'PERSIST_METADATA',
                            payload: {
                                savedId,
                                status: result?.status,
                            },
                        });
                        if (result) {
                            dispatch({ type: 'SET_CAMPAIGN_FOOTER_FROM_API', payload: result });
                        }
                        dispatch({
                            type: 'SET_LAST_PERSISTED_CAMPAIGN',
                            payload: buildLastPersistedFromCampaignFields({
                                name: payload.name,
                                senderId: payload.senderId,
                                subject: payload.subject,
                                body: payload.body,
                                status: result?.status ?? payload.status,
                                previewText: payload.previewText,
                                metadata: payload.metadata ?? null,
                            }),
                        });
                        navigate(`${CAMPAIGNS_MARKETING.route}/edit/${savedId}`, {
                            replace: true,
                        });
                        return savedId;
                    })();

                    pendingMarketingCreateRef.current = createWork;
                    createWork.finally(() => {
                        if (pendingMarketingCreateRef.current === createWork) {
                            pendingMarketingCreateRef.current = null;
                        }
                    });

                    const savedId = await createWork;

                    logMarketingCampaignsEvent({ submodule: 'campaign', functionality: 'create' });

                    return {
                        ...buildCampaignPayload({ ...overrides, id: savedId }),
                        id: savedId,
                    };
                }

                const result = await EntityCrudService.update(CAMPAIGNS_MARKETING, payload);
                const savedId = getSavedCampaignId(result) || payload.id;

                dispatch({
                    type: 'PERSIST_METADATA',
                    payload: {
                        savedId,
                        status: result?.status,
                    },
                });

                if (result) {
                    dispatch({ type: 'SET_CAMPAIGN_FOOTER_FROM_API', payload: result });
                }

                dispatch({
                    type: 'SET_LAST_PERSISTED_CAMPAIGN',
                    payload: buildLastPersistedFromCampaignFields({
                        name: payload.name,
                        senderId: payload.senderId,
                        subject: payload.subject,
                        body: payload.body,
                        status: result?.status ?? payload.status,
                        previewText: payload.previewText,
                        metadata: payload.metadata ?? null,
                    }),
                });

                return {
                    ...payload,
                    id: savedId || payload.id,
                };
            } catch (error) {
                if (showErrorToast) {
                    showGenericBackendErrorToast();
                }
                throw error;
            }
        },
        [buildCampaignPayload, dispatch, getSavedCampaignId, navigate],
    );

    const commitCampaignDesign = useCallback(
        async ({
            title,
            html,
            metadata,
            clearTemplateFlow = false,
            closeEditor = null,
            showSuccessToast = false,
        }) => {
            const setTitleAndHtmlPayload = { title, html };
            if (metadata !== undefined) {
                setTitleAndHtmlPayload.metadata = metadata;
            }

            dispatch({ type: 'SET_TITLE_AND_HTML', payload: setTitleAndHtmlPayload });

            if (clearTemplateFlow) {
                dispatch({ type: 'CLEAR_TEMPLATE_DESIGN_FLOW' });
            }

            const shouldPersistDesign = !clearTemplateFlow || Boolean(campaignId);

            if (shouldPersistDesign) {
                try {
                    const designUnchanged =
                        campaignId &&
                        isCampaignDesignUnchanged({
                            title,
                            html,
                            metadata,
                            lastPersisted,
                        });

                    if (!designUnchanged) {
                        const persistOverrides = { name: title, body: html };
                        if (metadata !== undefined) {
                            persistOverrides.metadata = metadata;
                        }
                        await persistCampaign(persistOverrides);
                        if (showSuccessToast) {
                            successToast({
                                text: getLiteral('label_success_save'),
                            });
                        }
                    }
                } catch {
                    /* toast: showGenericBackendErrorToast en persistCampaign */
                }
            }

            if (closeEditor) {
                dispatch({ type: closeEditor });
            }
        },
        [campaignId, lastPersisted, persistCampaign],
    );

    const hasExpandedSection = useMemo(() => {
        return Object.values(expandedSections).some(Boolean);
    }, [expandedSections]);
    const loggedUserEmail = useMemo(() => {
        return Context.store.getState()?.config?.userData?.email || '';
    }, []);
    const selectedSenderOption = useMemo(() => {
        return senders.find((sender) => String(sender.value) === String(selectedSenderId));
    }, [selectedSenderId, senders]);

    const selectedSenderEmail = useMemo(() => {
        return selectedSenderOption?.label || senderEmail || '';
    }, [selectedSenderOption, senderEmail]);

    const senderDisplayName = useMemo(() => {
        return (selectedSenderOption?.subLabel || '').trim();
    }, [selectedSenderOption]);

    const footerPreview = useMemo(
        () =>
            buildCampaignFooterPreview({
                footerSenderName,
                footerSenderAddress,
                footerFooterHere,
                selectedSenderOption,
            }),
        [footerSenderName, footerSenderAddress, footerFooterHere, selectedSenderOption],
    );
    const campaignHtmlWithFooterPreview = useMemo(
        () => applyFixedFooterPreviewToHtml(campaignHtml, footerPreview),
        [campaignHtml, footerPreview],
    );
    const previewModalHtml = useMemo(
        () => applyFixedFooterPreviewToHtml(previewCampaign?.body ?? campaignHtml, footerPreview),
        [campaignHtml, footerPreview, previewCampaign?.body],
    );

    const currentCampaign = useMemo(
        () => ({
            id: campaignId,
            name: campaignTitle,
            senderId: normalizeIdValue(selectedSenderId),
            subject,
            body: campaignHtml,
            status: campaignStatusValue,
            token,
            totalRecipients: recipientsCount,
        }),
        [
            campaignHtml,
            campaignTitle,
            campaignId,
            campaignStatusValue,
            normalizeIdValue,
            recipientsCount,
            selectedSenderId,
            subject,
            token,
        ],
    );

    // Extract Topol JSON from campaign metadata for the editor
    const campaignTopolJson = useMemo(
        () => extractTopolJsonFromMetadata(campaignMetadata),
        [campaignMetadata],
    );

    const htmlEditorInitialHtml =
        isTemplateDesignFlow && isHtmlEditorOpen
            ? (pendingTemplateDesign?.html ?? '')
            : campaignHtml;

    const htmlEditorInitialTitle =
        isTemplateDesignFlow && isHtmlEditorOpen
            ? (pendingTemplateDesign?.title ?? campaignTitle)
            : campaignTitle;

    const topolEditorEmailConfig =
        isTemplateDesignFlow && isTopolEditorOpen
            ? extractTopolJsonFromMetadata(pendingTemplateDesign?.metadata)
            : campaignTopolJson;

    const topolEditorTitle =
        isTemplateDesignFlow && isTopolEditorOpen
            ? (pendingTemplateDesign?.title ?? campaignTitle)
            : campaignTitle;

    const completedSections = useMemo(
        () => ({
            sender: Boolean(selectedSenderId),
            recipients: savedRecipientsCount > 0,
            subject: subject.trim().length > 0,
            design: campaignHtml?.trim?.().length > 0,
            additionalSettings: false,
            name: (lastPersisted?.name || '').trim().length > 0,
        }),
        [campaignHtml, lastPersisted, savedRecipientsCount, selectedSenderId, subject],
    );

    const displayedRecipientsCount = expandedSections.recipients
        ? recipientsCount
        : savedRecipientsCount;

    const isSendDisabledByContent = useMemo(
        () =>
            !(
                completedSections.sender &&
                completedSections.recipients &&
                completedSections.subject &&
                completedSections.design &&
                completedSections.name
            ),
        [completedSections],
    );

    const isSendBlockedByDomain = !domainValidationLoading && !hasValidatedDomain;

    const isSendBlockedByLimits = useMemo(
        () => getCampaignSendLimitBlock(campaignLimitsData, savedRecipientsCount).blocked,
        [campaignLimitsData, savedRecipientsCount],
    );

    const isSendDisabled =
        isSendDisabledByContent ||
        domainValidationLoading ||
        campaignLimitsLoading ||
        isSendBlockedByDomain ||
        isSendBlockedByLimits;

    const hasExpandedSectionForDimming = useMemo(() => {
        const es = expandedSections;
        if (es.sender || es.recipients || es.subject) return true;
        if (es.additionalSettings) return true;
        if (es.design && !completedSections.design) return true;
        return false;
    }, [expandedSections, completedSections]);

    const handleToggleSection = useCallback((section) => {
        dispatch({ type: 'TOGGLE_SECTION', payload: section });
    }, []);

    const handleBack = useCallback(() => {
        if (onBack) {
            onBack();
            return;
        }

        navigate(CAMPAIGNS_MARKETING.route);
    }, [navigate, onBack]);

    const openPreviewModal = useCallback(
        (nextCampaign) => {
            dispatch({ type: 'OPEN_PREVIEW_MODAL', payload: nextCampaign });
            onPreview?.(nextCampaign);
        },
        [onPreview],
    );

    const handlePreview = useCallback(() => {
        openPreviewModal(currentCampaign);
    }, [currentCampaign, openPreviewModal]);

    const handleHtmlEditorPreview = useCallback(
        (previewData = {}) => {
            const previewHtml = applyFixedFooterPreviewToHtml(
                previewData.html ?? previewData.body ?? currentCampaign.body,
                footerPreview,
            );
            const nextCampaign = {
                ...currentCampaign,
                name: previewData.title ?? currentCampaign.name,
                body: previewHtml,
            };

            openPreviewModal(nextCampaign);
        },
        [currentCampaign, footerPreview, openPreviewModal],
    );

    const handleTopolEditorPreview = useCallback(
        (previewData = {}) => {
            const nextCampaign = {
                ...currentCampaign,
                body: previewData.html ?? currentCampaign.body,
                name: previewData.title ?? currentCampaign.name,
            };
            openPreviewModal(nextCampaign);
        },
        [currentCampaign, openPreviewModal],
    );

    const handleClosePreviewModal = useCallback(() => {
        dispatch({ type: 'CLOSE_PREVIEW_MODAL' });
    }, []);

    const handlePreviewModalAfterClose = useCallback(() => {
        dispatch({ type: 'CLEAR_PREVIEW_CAMPAIGN' });
    }, []);

    const handleSendTestEmail = useCallback(
        (recipient) => {
            onSendTestEmail?.(recipient);
        },
        [onSendTestEmail],
    );

    const handleSend = useCallback(() => {
        if (isSendDisabled) {
            return;
        }

        setIsSendConsentChecked(false);
        setIsSendConfirmOpen(true);
    }, [isSendDisabled]);

    const handleCloseSendConfirm = useCallback(() => {
        if (isSendSubmitting) {
            return;
        }

        setIsSendConfirmOpen(false);
        setIsSendConsentChecked(false);
    }, [isSendSubmitting]);

    const handleSendConsentChange = useCallback(() => {
        setIsSendConsentChecked((currentValue) => !currentValue);
    }, []);

    const handleConfirmSend = useCallback(async () => {
        if (isSendDisabled || !isSendConsentChecked || isSendSubmitting) {
            return;
        }

        setIsSendSubmitting(true);

        try {
            let persistedCampaign = currentCampaign;

            if (!campaignId) {
                persistedCampaign = await persistCampaign({}, { showErrorToast: false });
            }

            await MailingService.launchCampaign(persistedCampaign.id ?? campaignId);
            publish(`${REFRESH_TABLE}_${CAMPAIGNS_MARKETING.entity}`);

            logMarketingCampaignsEvent({ functionality: 'sendEmail' });

            onSend?.({
                ...currentCampaign,
                status: MARKETING_CAMPAIGN_STATUS.SENDING,
            });

            setIsSendConfirmOpen(false);
            setIsSendConsentChecked(false);
            successToast({
                title: getLiteral('title_toast_marketing_campaigns_send_success'),
                text: getLiteral('label_toast_marketing_campaigns_send_success_message'),
            });
            navigate(CAMPAIGNS_MARKETING.route);
        } catch {
            errorToast({
                title: getLiteral('title_toast_marketing_campaigns_send_error'),
                text: getLiteral('label_toast_marketing_campaigns_send_error_message'),
            });
        } finally {
            setIsSendSubmitting(false);
        }
    }, [
        campaignId,
        currentCampaign,
        isSendConsentChecked,
        isSendDisabled,
        isSendSubmitting,
        navigate,
        onSend,
        persistCampaign,
    ]);

    const handleAddRecipients = useCallback(() => {
        dispatch({ type: 'OPEN_SELECT_RECIPIENTS' });
    }, []);

    const handleCancelRecipients = useCallback(() => {
        dispatch({ type: 'CANCEL_SECTION', payload: 'recipients' });
    }, []);

    const handleRecipientsModalDone = useCallback((token, count) => {
        dispatch({ type: 'SET_RECIPIENTS_DATA', payload: { token, count } });
    }, []);

    const handleRecipientsModalCancel = useCallback(() => {
        dispatch({ type: 'CLOSE_SELECT_RECIPIENTS' });
    }, []);

    const handleSaveRecipients = useCallback(async () => {
        setIsSectionSaveLoading(true);
        try {
            dispatch({ type: 'COMMIT_RECIPIENTS_SAVE' });
            await persistCampaign({ token, totalRecipients: recipientsCount });
        } catch {
            /* toast: showGenericBackendErrorToast en persistCampaign */
        } finally {
            setIsSectionSaveLoading(false);
        }
    }, [dispatch, persistCampaign, recipientsCount, token]);

    const handleSubjectChange = useCallback((value) => {
        dispatch({ type: 'SET_SUBJECT', payload: value });
    }, []);

    const handlePreviewTextChange = useCallback((value) => {
        dispatch({ type: 'SET_PREVIEW_TEXT', payload: value });
    }, []);

    const handleSaveSubject = useCallback(
        async (fields) => {
            const nextSubject = (fields?.subject ?? subject).trim();
            const nextPreviewText = (fields?.previewText ?? previewText ?? '').trim();
            const subjectUnchanged =
                campaignId &&
                lastPersisted &&
                nextSubject === (lastPersisted.subject || '').trim() &&
                nextPreviewText === (lastPersisted.previewText || '').trim();
            if (subjectUnchanged) {
                dispatch({ type: 'COMMIT_SECTION', payload: 'subject' });
                return;
            }

            setIsSectionSaveLoading(true);
            try {
                await persistCampaign({ subject: nextSubject, previewText: nextPreviewText });
                dispatch({ type: 'COMMIT_SECTION', payload: 'subject' });
            } catch {
                /* toast: showGenericBackendErrorToast en persistCampaign */
            } finally {
                setIsSectionSaveLoading(false);
            }
        },
        [campaignId, dispatch, lastPersisted, persistCampaign, previewText, subject],
    );

    const handleTitleSave = useCallback(
        async (newTitle) => {
            try {
                const trimmed = typeof newTitle === 'string' ? newTitle.trim() : newTitle;
                const titleUnchanged =
                    campaignId && lastPersisted && trimmed === (lastPersisted.name || '').trim();
                if (titleUnchanged) {
                    return;
                }
                dispatch({ type: 'SET_CAMPAIGN_TITLE', payload: newTitle });
                await persistCampaign({ name: newTitle });
            } catch {
                /* toast: showGenericBackendErrorToast en persistCampaign */
            }
        },
        [campaignId, lastPersisted, persistCampaign],
    );

    const handleDesignOptionSelect = useCallback((option) => {
        if (option?.id === 'html_editor') {
            dispatch({ type: 'OPEN_HTML_EDITOR' });
        } else if (option?.id === 'create_new') {
            dispatch({ type: 'OPEN_TOPOL_EDITOR' });
        } else if (option?.id === 'your_templates') {
            dispatch({ type: 'OPEN_TEMPLATE_SELECTOR' });
        }
    }, []);

    const handleCloseTemplateSelector = useCallback(() => {
        dispatch({ type: 'CLOSE_TEMPLATE_SELECTOR' });
    }, []);

    const handleSelectCampaignTemplate = useCallback(
        (row) => {
            logMarketingCampaignsEvent({ submodule: 'templates', functionality: 'select' });
            beginTemplateDesignFlow(row, TEMPLATE_DESIGN_FLOW_ORIGIN.CAMPAIGN_SELECTOR);
        },
        [beginTemplateDesignFlow],
    );

    const restoreDesignSnapshotBeforeTemplate = useCallback(() => {
        const snapshot = designSnapshotBeforeTemplate;
        if (!snapshot) {
            return;
        }
        dispatch({
            type: 'SET_TITLE_AND_HTML',
            payload: {
                title: snapshot.title,
                html: snapshot.html,
                metadata: snapshot.metadata,
            },
        });
    }, [designSnapshotBeforeTemplate]);

    const finishTemplateDesignFlowExit = useCallback(
        (discardChanges) => {
            if (!discardChanges) {
                return;
            }

            const origin = templateDesignFlowOrigin;
            restoreDesignSnapshotBeforeTemplate();
            dispatch({ type: 'CLEAR_TEMPLATE_DESIGN_FLOW' });

            if (origin === TEMPLATE_DESIGN_FLOW_ORIGIN.CAMPAIGN_SELECTOR) {
                dispatch({ type: 'OPEN_TEMPLATE_SELECTOR' });
            } else if (origin === TEMPLATE_DESIGN_FLOW_ORIGIN.TEMPLATES_LIST) {
                navigate(MARKETING_TEMPLATES_PATHS.BASE_LIST);
            }
        },
        [navigate, restoreDesignSnapshotBeforeTemplate, templateDesignFlowOrigin],
    );

    const handleEditDesign = useCallback(() => {
        if (hasTopolEditorMetadata(campaignMetadata)) {
            dispatch({ type: 'OPEN_TOPOL_EDITOR' });
        } else {
            dispatch({ type: 'OPEN_HTML_EDITOR' });
        }
    }, [campaignMetadata]);

    const handleHtmlEditorExit = useCallback(
        (discardChanges = true) => {
            if (isTemplateDesignFlow) {
                finishTemplateDesignFlowExit(discardChanges);
            }
            dispatch({ type: 'CLOSE_HTML_EDITOR' });
        },
        [finishTemplateDesignFlowExit, isTemplateDesignFlow],
    );

    const handleTopolEditorExit = useCallback(() => {
        if (isTemplateDesignFlow) {
            finishTemplateDesignFlowExit(true);
        }
        dispatch({ type: 'CLOSE_TOPOL_EDITOR' });
    }, [finishTemplateDesignFlowExit, isTemplateDesignFlow]);

    const handleTopolEditorSave = useCallback(
        async ({ html, topolJson }) => {
            const nextMetadata = mergeTopolJsonIntoCampaignMetadata(campaignMetadata, topolJson);
            const isFirstDesignSave = !lastPersisted?.body?.trim();

            await commitCampaignDesign({
                title: campaignTitle,
                html,
                metadata: nextMetadata,
                clearTemplateFlow: isTemplateDesignFlow,
                closeEditor: 'CLOSE_TOPOL_EDITOR',
            });

            if (isFirstDesignSave) {
                logMarketingCampaignsEvent({
                    submodule: 'campaign',
                    functionality: 'createVisualEditor',
                });
            }
        },
        [
            campaignMetadata,
            campaignTitle,
            commitCampaignDesign,
            isTemplateDesignFlow,
            lastPersisted?.body,
        ],
    );

    const handleCloseSaveAsTemplateModal = useCallback(() => {
        if (isSaveAsTemplateSubmitting) {
            return;
        }
        setIsSaveAsTemplateModalOpen(false);
        setPendingTemplateDraft(null);
    }, [isSaveAsTemplateSubmitting]);

    const handleRequestSaveAsTemplate = useCallback(({ html, title, topolJson }) => {
        setPendingTemplateDraft({ html, title, topolJson });
        setIsSaveAsTemplateModalOpen(true);
    }, []);

    const handleSaveDesignAsTemplate = useCallback(() => {
        if (!campaignHtml?.trim()) {
            return;
        }

        handleRequestSaveAsTemplate({
            html: campaignHtml,
            title: campaignTitle,
            topolJson: campaignTopolJson ?? undefined,
        });
    }, [campaignHtml, campaignTitle, campaignTopolJson, handleRequestSaveAsTemplate]);

    const handleConfirmSaveAsTemplate = useCallback(
        async (templateName) => {
            if (!pendingTemplateDraft || isSaveAsTemplateSubmitting) {
                return;
            }

            const payload = {
                name: templateName,
                html: pendingTemplateDraft.html || '',
                ...(pendingTemplateDraft.topolJson
                    ? { topolConfig: pendingTemplateDraft.topolJson }
                    : {}),
            };

            setIsSaveAsTemplateSubmitting(true);

            try {
                await createMarketingEmailTemplate(payload);
                logMarketingCampaignsEvent({
                    submodule: 'templates',
                    functionality: 'saveAsTemplate',
                });
                successToast({
                    title: getLiteral('label_marketing_campaigns_design_save_as_template'),
                    text: getLiteral('label_marketing_campaigns_design_save_as_template_success'),
                });
                setIsSaveAsTemplateModalOpen(false);
                setPendingTemplateDraft(null);
            } catch {
                showGenericBackendErrorToast();
            } finally {
                setIsSaveAsTemplateSubmitting(false);
            }
        },
        [isSaveAsTemplateSubmitting, pendingTemplateDraft],
    );

    const handleHtmlEditorSave = useCallback(
        async ({ html, title }) => {
            const isFirstDesignSave = !lastPersisted?.body?.trim();

            await commitCampaignDesign({
                title,
                html,
                metadata: isTemplateDesignFlow ? null : undefined,
                clearTemplateFlow: isTemplateDesignFlow,
                closeEditor: isTemplateDesignFlow ? 'CLOSE_HTML_EDITOR' : null,
                showSuccessToast: !isTemplateDesignFlow,
            });

            if (isFirstDesignSave) {
                logMarketingCampaignsEvent({
                    submodule: 'campaign',
                    functionality: 'createHTMLEditor',
                });
            }
        },
        [commitCampaignDesign, isTemplateDesignFlow, lastPersisted?.body],
    );

    const handleOpenResetDesignConfirm = useCallback(() => {
        setIsResetDesignConfirmOpen(true);
    }, []);

    const handleDownloadHtml = useCallback(
        (html) => {
            const filename = sanitizeFilename(campaignTitle || 'campaign');
            downloadFile(html, `${filename}.html`, 'text/html');
        },
        [campaignTitle],
    );

    const handleCloseResetDesignConfirm = useCallback(() => {
        setIsResetDesignConfirmOpen(false);
    }, []);

    const handleConfirmResetDesign = useCallback(async () => {
        try {
            const resetUnchanged =
                campaignId &&
                lastPersisted &&
                (campaignTitle || '').trim() === (lastPersisted.name || '').trim() &&
                (lastPersisted.body || '') === '' &&
                stableStringifyMetadata(lastPersisted.metadata) === '';

            if (!resetUnchanged) {
                await persistCampaign({ body: '', metadata: null });
            }

            skipNextCampaignFetchRef.current = true;

            if (templateDesignFlowOrigin) {
                dispatch({ type: 'CLEAR_TEMPLATE_DESIGN_FLOW' });
            }
            if (isTemplateSelectorOpen) {
                dispatch({ type: 'CLOSE_TEMPLATE_SELECTOR' });
            }
            if (isHtmlEditorOpen) {
                dispatch({ type: 'CLOSE_HTML_EDITOR' });
            } else if (isTopolEditorOpen) {
                dispatch({ type: 'CLOSE_TOPOL_EDITOR' });
            }
            dispatch({
                type: 'SET_TITLE_AND_HTML',
                payload: { title: campaignTitle, html: '' },
            });
            dispatch({ type: 'CLEAR_TOPOL_METADATA' });
            setIsResetDesignConfirmOpen(false);
        } catch {
            /* toast: showGenericBackendErrorToast en persistCampaign */
        }
    }, [
        campaignId,
        campaignTitle,
        lastPersisted,
        persistCampaign,
        isHtmlEditorOpen,
        isTemplateSelectorOpen,
        isTopolEditorOpen,
        templateDesignFlowOrigin,
    ]);

    const handleSaveSender = useCallback(
        async ({ senderId }) => {
            dispatch({
                type: 'SAVE_SENDER_FIELDS',
                payload: { senderId },
            });

            const senderUnchanged =
                campaignId &&
                lastPersisted &&
                comparablePersistId(senderId) === comparablePersistId(lastPersisted.senderId);
            if (senderUnchanged) {
                dispatch({ type: 'COMMIT_SECTION', payload: 'sender' });
                return;
            }

            setIsSectionSaveLoading(true);
            try {
                await persistCampaign({ senderId });
                dispatch({ type: 'COMMIT_SECTION', payload: 'sender' });
            } catch {
                /* toast: showGenericBackendErrorToast en persistCampaign */
            } finally {
                setIsSectionSaveLoading(false);
            }
        },
        [campaignId, dispatch, lastPersisted, persistCampaign],
    );

    const handleOpenSenderModal = useCallback(() => {
        dispatch({ type: 'OPEN_SENDER_MODAL' });
    }, []);

    const handleCloseSenderModal = useCallback(() => {
        dispatch({ type: 'CLOSE_SENDER_MODAL' });
    }, []);

    const handleSenderSuccess = useCallback(
        async (responseData) => {
            const createdSenderId = getMarketingSenderIdFromRow(responseData);
            const ensureEntries = createdSenderId
                ? [{ row: responseData, id: createdSenderId }]
                : [];

            try {
                await fetchAndApplySenderOptions({
                    ensureEntries,
                    preferredSenderId: createdSenderId ?? undefined,
                });
            } catch {
                if (createdSenderId) {
                    applySenderOptions(
                        mergeEnsureSendersIntoSelectOptions(senders, ensureEntries),
                        createdSenderId,
                    );
                    return;
                }
                showGenericBackendErrorToast();
            }
        },
        [applySenderOptions, fetchAndApplySenderOptions, senders],
    );

    const handleSenderSelectionChange = useCallback((senderId) => {
        if (senderId === null || senderId === undefined || senderId === '') {
            return;
        }
        let next = senderId;
        if (typeof next === 'object') {
            next = next.value ?? next.id ?? next.senderId;
        }
        if (next === null || next === undefined || next === '') {
            return;
        }
        dispatch({
            type: 'SET_SELECTED_SENDER_ID',
            payload: String(next),
        });
    }, []);

    const campaignStatusLabel = getMarketingCampaignStatusLabel(currentCampaign.status);
    const badgeType = getMarketingCampaignStatusBadgeType(currentCampaign.status);
    const shouldShowHtmlEditor = isHtmlEditorOpen;
    const containerClassName = classNames('campaign-details__container', {
        'campaign-details__container--expanded': hasExpandedSection,
        'campaign-details__container--html-editor': shouldShowHtmlEditor,
    });
    const isBootstrapping =
        ((routeCampaignId && !editBootstrapComplete) || !sendersLoadComplete) &&
        !isHtmlEditorOpen &&
        !isTopolEditorOpen &&
        !isTemplateSelectorOpen;

    const formContent = (
        <CampaignDetailsFormContent
            completedSections={completedSections}
            expandedSections={expandedSections}
            hasExpandedSectionForDimming={hasExpandedSectionForDimming}
            isSectionsDisabled={isBootstrapping}
            recipientsCount={displayedRecipientsCount}
            senderDisplayName={senderDisplayName}
            senderEmail={selectedSenderEmail}
            senders={senders}
            selectedSenderId={selectedSenderId}
            subject={subject}
            previewText={previewText}
            theme={theme}
            onAddRecipients={handleAddRecipients}
            onCancelRecipients={handleCancelRecipients}
            onDesignOptionSelect={handleDesignOptionSelect}
            onEditDesign={handleEditDesign}
            onOpenSenderModal={handleOpenSenderModal}
            onPreviewTextChange={handlePreviewTextChange}
            onSaveRecipients={handleSaveRecipients}
            onSaveSender={handleSaveSender}
            onSaveSubject={handleSaveSubject}
            isSectionSaveLoading={isSectionSaveLoading}
            onSelectedSenderChange={handleSenderSelectionChange}
            onSubjectChange={handleSubjectChange}
            onToggleSection={handleToggleSection}
            onResetDesign={handleOpenResetDesignConfirm}
            onDownloadHtml={handleDownloadHtml}
            onSaveAsTemplate={handleSaveDesignAsTemplate}
            senderSelectInstanceKey={senderSelectInstanceKey}
            campaignHtml={campaignHtmlWithFooterPreview}
        />
    );

    if (isBootstrapping) {
        return (
            <div className="campaign-details">
                <DomainVerificationBanner />
                <div className="campaign-details__main">
                    <CampaignDetailsHeader
                        badgeType={badgeType}
                        campaignTitle={campaignTitle}
                        isSendBlockedByDomain={isSendBlockedByDomain}
                        isSendDisabled={isSendDisabled}
                        onBack={handleBack}
                        onPreview={handlePreview}
                        onSend={handleSend}
                        onTitleChange={(value) =>
                            dispatch({ type: 'SET_CAMPAIGN_TITLE', payload: value })
                        }
                        onTitleSave={handleTitleSave}
                        status={campaignStatusLabel}
                    />

                    <div className={containerClassName}>
                        <div className="campaign-details__content">{formContent}</div>
                    </div>
                    <div className="campaign-details__bootstrap-loader">
                        <AbsoluteLoader size="massive" />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="campaign-details">
            <div className="campaign-details__main">
                <DomainVerificationBanner />
                <CampaignDetailsHeader
                    badgeType={badgeType}
                    campaignTitle={campaignTitle}
                    isSendBlockedByDomain={isSendBlockedByDomain}
                    isSendDisabled={isSendDisabled}
                    onBack={handleBack}
                    onPreview={handlePreview}
                    onSend={handleSend}
                    onTitleChange={(value) =>
                        dispatch({ type: 'SET_CAMPAIGN_TITLE', payload: value })
                    }
                    onTitleSave={handleTitleSave}
                    status={campaignStatusLabel}
                />

                <div className={containerClassName}>
                    <div className="campaign-details__content">{formContent}</div>
                </div>
            </div>

            <div
                className={classNames('campaign-details__template-selector-overlay', {
                    'campaign-details__template-selector-overlay--open': isTemplateSelectorOpen,
                })}
                aria-hidden={!isTemplateSelectorOpen}
            >
                <CampaignTemplateSelector
                    isOpen={isTemplateSelectorOpen}
                    footerPreview={footerPreview}
                    onBack={handleCloseTemplateSelector}
                    onSelectTemplate={handleSelectCampaignTemplate}
                />
            </div>

            <div
                className={classNames('campaign-details__editor-overlay', {
                    'campaign-details__editor-overlay--open': shouldShowHtmlEditor,
                })}
                aria-hidden={!shouldShowHtmlEditor}
            >
                <HtmlEditorOverlay
                    campaignId={campaignId}
                    initialHtml={htmlEditorInitialHtml}
                    initialTitle={htmlEditorInitialTitle}
                    isOpen={shouldShowHtmlEditor}
                    footerPreview={footerPreview}
                    onPreview={handleHtmlEditorPreview}
                    onSave={handleHtmlEditorSave}
                    onSaveAsTemplate={handleRequestSaveAsTemplate}
                    onExitWithoutSaving={handleHtmlEditorExit}
                />
            </div>

            <div
                className={classNames('campaign-details__editor-overlay', {
                    'campaign-details__editor-overlay--open': isTopolEditorOpen,
                })}
                aria-hidden={!isTopolEditorOpen}
            >
                <EmailEditor
                    isOpen={isTopolEditorOpen}
                    emailConfig={topolEditorEmailConfig}
                    title={topolEditorTitle}
                    footerPreview={footerPreview}
                    onSave={handleTopolEditorSave}
                    onSaveAsTemplate={handleRequestSaveAsTemplate}
                    onExit={handleTopolEditorExit}
                    onPreview={handleTopolEditorPreview}
                />
            </div>

            <SelectRecipientsModal
                isOpen={isSelectRecipientsOpen}
                campaignId={campaignId}
                token={token}
                onDone={handleRecipientsModalDone}
                onCancel={handleRecipientsModalCancel}
            />
            <SenderFormModal
                isOpen={isSenderModalOpen}
                onCancel={handleCloseSenderModal}
                onSuccess={handleSenderSuccess}
                onRequestClose={handleCloseSenderModal}
            />
            <CampaignPreviewTestModal
                isOpen={isPreviewModalOpen}
                onRequestClose={handleClosePreviewModal}
                onAfterClose={handlePreviewModalAfterClose}
                from={selectedSenderEmail}
                subject={subject}
                previewText={previewText}
                htmlPreview={previewModalHtml}
                defaultRecipient={loggedUserEmail}
                onSendTestEmail={handleSendTestEmail}
            />
            <CampaignSendConfirmModal
                isConsentChecked={isSendConsentChecked}
                isOpen={isSendConfirmOpen}
                isSubmitting={isSendSubmitting}
                onConsentChange={handleSendConsentChange}
                onConfirm={handleConfirmSend}
                onRequestClose={handleCloseSendConfirm}
                recipientsCount={savedRecipientsCount}
            />
            <ModalHoi
                isOpen={isResetDesignConfirmOpen}
                onRequestClose={handleCloseResetDesignConfirm}
                onCancel={handleCloseResetDesignConfirm}
                onConfirm={handleConfirmResetDesign}
                title={getLiteral('label_marketing_campaigns_design_reset_design_confirmation')}
                cancelText={getLiteral('action_cancel')}
                confirmText={getLiteral('action_confirm')}
                type="confirm"
            >
                <Text type="body">
                    {getLiteral('label_marketing_campaigns_design_reset_design_confirmation_desc')}
                </Text>
            </ModalHoi>
            <SaveAsTemplateModal
                isOpen={isSaveAsTemplateModalOpen}
                isSubmitting={isSaveAsTemplateSubmitting}
                defaultName=""
                onCancel={handleCloseSaveAsTemplateModal}
                onRequestClose={handleCloseSaveAsTemplateModal}
                onConfirm={handleConfirmSaveAsTemplate}
            />
        </div>
    );
});

MarketingCampaignsForm.displayName = 'MarketingCampaignsForm';

MarketingCampaignsForm.propTypes = {
    onBack: PropTypes.func,
    onPreview: PropTypes.func,
    onSend: PropTypes.func,
    onSendTestEmail: PropTypes.func,
};

export default MarketingCampaignsForm;
